import TechnoArtLogo from "../components/technoArtLogo.tsx";
import { Trash2, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Background from "../components/background.tsx";
import AccessListTab from "../components/config/access-list-tab.tsx";
import DetectionAreaTab from "../components/config/detection-area-tab.tsx";
import LocationManagementTab from "../components/config/location-management-tab.tsx";
import Header from "../components/header.tsx";
import Sidebar from "../components/sidebar.tsx";

const LOCATION_OPTIONS: LocationOption[] = [
  { name: "Gate 1 - Entrance", type: "Entry" },
  { name: "Gate 2 - Entrance", type: "Entry" },
  { name: "Gate 3 - Exit", type: "Exit" },
  { name: "Gate 4 - Exit", type: "Exit" },
];

type Point = { x: number; y: number };
type LocationOption = { name: string; type: "Entry" | "Exit" };

const DetectionConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"area" | "list" | "location">(
    "area"
  );

  // Blacklist/Whitelist state
  const [plates, setPlates] = useState<
    { plate: string; type: "blacklist" | "whitelist" }[]
  >([
    { plate: "B1234XYZ", type: "blacklist" },
    { plate: "D5678ABC", type: "whitelist" },
  ]);
  const [newPlate, setNewPlate] = useState("");
  const [newType, setNewType] = useState<"blacklist" | "whitelist">(
    "blacklist"
  );
  const [selectedLocation, setSelectedLocation] = useState<string>(
    LOCATION_OPTIONS[0].name
  );
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Store the current camera stream for cleanup
  const streamRef = useRef<MediaStream | null>(null);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>(
    LOCATION_OPTIONS
  );
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState("");
  const [newLocationType, setNewLocationType] = useState<"Entry" | "Exit">(
    "Entry"
  );

  // Detection area points state
  const [areaPoints, setAreaPoints] = useState<Point[]>([
    { x: 150, y: 300 },
    { x: 450, y: 280 },
    { x: 500, y: 350 },
    { x: 100, y: 370 },
  ]);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  // Use a ref to always have the latest draggingIdx in event listeners
  const draggingIdxRef = useRef<number | null>(null);
  draggingIdxRef.current = draggingIdx;

  // Move handler OUTSIDE render so it's stable
  const handleWindowPointerMove = useCallback((e: PointerEvent) => {
    if (draggingIdxRef.current === null) return;
    const svg = document.getElementById("detection-area-svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 600;
    const y = ((e.clientY - rect.top) / rect.height) * 400;
    setAreaPoints((pts) =>
      pts.map((pt, i) =>
        i === draggingIdxRef.current
          ? {
            x: Math.max(0, Math.min(600, x)),
            y: Math.max(0, Math.min(400, y)),
          }
          : pt
      )
    );
  }, []);

  const handleWindowPointerUp = useCallback(() => {
    setDraggingIdx(null);
    window.removeEventListener("pointermove", handleWindowPointerMove);
    window.removeEventListener("pointerup", handleWindowPointerUp);
  }, [handleWindowPointerMove]);

  const handlePointerDown = (idx: number) => () => {
    setDraggingIdx(idx);
    draggingIdxRef.current = idx;
    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
  };

  // Get connected video input devices on mount
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setCameraDevices(videoDevices);
      if (videoDevices.length > 0) {
        setSelectedCameraId((prev) => prev || videoDevices[0].deviceId);
      }
    });
  }, []);

  // Get camera stream when selectedCameraId changes and tab is "area"
  useEffect(() => {
    let stream: MediaStream;

    if (activeTab === "area" && selectedCameraId) {
      navigator.mediaDevices
        .getUserMedia({
          video: { deviceId: { exact: selectedCameraId } },
        })
        .then((mediaStream) => {
          stream = mediaStream;
          streamRef.current = mediaStream;
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        })
        .catch(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        });
    } else {
      // If not in "area" tab, turn off camera
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }

    // Cleanup: stop the stream when component unmounts or tab/camera changes
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [selectedCameraId, activeTab]);

  const handleSave = () => {
    const videoWidth = videoRef.current?.videoWidth || 600;
    const videoHeight = videoRef.current?.videoHeight || 400;

    // Preview frame size (SVG)
    const previewWidth = 600;
    const previewHeight = 400;

    // Scale points
    const scaledPoints = areaPoints.map((pt) => ({
      x: (pt.x / previewWidth) * videoWidth,
      y: (pt.y / previewHeight) * videoHeight,
    }));

    // Show result
    const formatted = scaledPoints
      .map(
        (pt, idx) =>
          `x${idx + 1}: ${pt.x.toFixed(1)}, y${idx + 1}: ${pt.y.toFixed(1)}`
      )
      .join("\n");
    alert("Detection area points (actual camera resolution):\n" + formatted);
  };

  const handleAddPlate = () => {
    if (newPlate.trim() !== "") {
      setPlates([...plates, { plate: newPlate.trim(), type: newType }]);
      setNewPlate("");
    }
  };

  const handleRemovePlate = (plate: string) => {
    setPlates(plates.filter((p) => p.plate !== plate));
  };

  // Handler for adding new location
  const handleAddLocation = () => {
    if (
      newLocation.trim() &&
      !locationOptions.some((loc) => loc.name === newLocation.trim())
    ) {
      setLocationOptions([
        ...locationOptions,
        { name: newLocation.trim(), type: newLocationType },
      ]);
      setSelectedLocation(newLocation.trim());
    }
    setNewLocation("");
    setShowAddLocation(false);
  };

  const [timestamp, setTimestamp] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(new Date());
    }, 1000); // update every second
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Background />

      {/* Add Location Overlay */}
      {showAddLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-sm relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              onClick={() => setShowAddLocation(false)}
            >
              <X size={24} />
            </button>
            <h2 className="text-lg font-bold mb-4">Add New Location</h2>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new location name"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              autoFocus
            />
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Location Type</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="Entry"
                    checked={newLocationType === "Entry"}
                    onChange={() => setNewLocationType("Entry")}
                    className="mr-2"
                  />
                  Entry
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="Exit"
                    checked={newLocationType === "Exit"}
                    onChange={() => setNewLocationType("Exit")}
                    className="mr-2"
                  />
                  Exit
                </label>
              </div>
            </div>
            <button
              className="w-full bg-blue-500 text-white py-2 rounded-lg font-semibold hover:bg-blue-600 transition mb-4"
              onClick={handleAddLocation}
              disabled={!newLocation.trim()}
            >
              Add Location
            </button>
            <div>
              <h3 className="font-semibold mb-2">Existing Locations</h3>
              <ul>
                {locationOptions.map((loc) => (
                  <li
                    key={loc.name}
                    className="flex items-center justify-between mb-1"
                  >
                    <div>
                      <span>{loc.name}</span>
                      <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
                        {loc.type}
                      </span>
                    </div>
                    <button
                      className="ml-2 text-red-500 hover:text-red-700"
                      onClick={() => {
                        setLocationOptions(
                          locationOptions.filter((l) => l.name !== loc.name)
                        );
                        if (
                          selectedLocation === loc.name &&
                          locationOptions.length > 1
                        ) {
                          setSelectedLocation(
                            locationOptions.find((l) => l.name !== loc.name)
                              ?.name || ""
                          );
                        }
                      }}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <div className="p-8">
            <div className="max-w-6xl mx-auto">
              {/* Card Header */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-t-2xl p-6 shadow-lg">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                  ALPR Configuration
                </h1>
                <p className="text-gray-600">
                  Manage detection areas, access lists, and location settings
                  for your cameras and gates.
                </p>
              </div>

              {/* Tabs */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-b-2xl p-0 shadow-lg">
                <div className="flex border-b">
                  <button
                    className={`px-6 py-3 font-medium focus:outline-none ${activeTab === "area"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500"
                      }`}
                    onClick={() => setActiveTab("area")}
                  >
                    Detection Area Setup
                  </button>
                  <button
                    className={`px-6 py-3 font-medium focus:outline-none ${activeTab === "list"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500"
                      }`}
                    onClick={() => setActiveTab("list")}
                  >
                    Access List Management
                  </button>
                  <button
                    className={`px-6 py-3 font-medium focus:outline-none ${activeTab === "location"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500"
                      }`}
                    onClick={() => setActiveTab("location")}
                  >
                    Location Management
                  </button>
                </div>
                <div className="p-8">
                  {activeTab === "area" && (
                    <DetectionAreaTab
                      locationOptions={locationOptions}
                      selectedLocation={selectedLocation}
                      setSelectedLocation={setSelectedLocation}
                      showAddLocation={showAddLocation}
                      setShowAddLocation={setShowAddLocation}
                      cameraDevices={cameraDevices}
                      selectedCameraId={selectedCameraId}
                      setSelectedCameraId={setSelectedCameraId}
                      videoRef={videoRef}
                      areaPoints={areaPoints}
                      handlePointerDown={handlePointerDown}
                      timestamp={timestamp}
                      handleSave={handleSave}
                    />
                  )}
                  {activeTab === "list" && (
                    <AccessListTab
                      plates={plates}
                      newPlate={newPlate}
                      setNewPlate={setNewPlate}
                      newType={newType}
                      setNewType={setNewType}
                      handleAddPlate={handleAddPlate}
                      handleRemovePlate={handleRemovePlate}
                    />
                  )}
                  {activeTab === "location" && (
                    <LocationManagementTab
                      selectedLocation={selectedLocation}
                      setSelectedLocation={setSelectedLocation}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>
      <TechnoArtLogo />
    </div>
  );
};

export default DetectionConfigPage;
